import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import { collection, getDocs, getDoc, query, orderBy, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { buildManualSubscription } from '../../utils/manualSubscription';
import { filterByDateRange } from '../../utils/dateRangeFilter';

interface User {
  id: string;
  email: string;
  displayName?: string;
  createdAt?: Date;
  subscription?: {
    plan: string;
    status: string;
    provider?: string;
    stripeSubscriptionId?: string;
    lifetime?: boolean;
  };
}

export const AdminUsers: React.FC = () => {
  const { theme } = useTheme();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [planFilter, setPlanFilter] = useState<'all' | 'free' | 'basic' | 'pro'>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({
    plan: 'free',
    status: 'inactive',
    durationInMonths: '1',
    lifetime: false,
  });

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const adminToken = localStorage.getItem('adminToken');
      if (!adminToken) {
        setError('Acesso não autorizado');
        setLoading(false);
        return;
      }

      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(query(usersRef, orderBy('createdAt', 'desc')));

      // Antes: N+1 sequencial (1 subscription + 1 sales por usuário, um de cada vez).
      // Agora: sales numa query só + leitura do doc subscription/current de todos os
      // usuários EM PARALELO. Mesmo nº de reads, mas concorrente em vez de sequencial.
      const subByUser: Record<string, any> = {};
      const providerByUser: Record<string, string> = {};

      const salesPromise = getDocs(collection(db, 'sales'))
        .then((salesSnap) => {
          salesSnap.forEach((d) => {
            const s = d.data();
            if (s.userId && s.provider && !providerByUser[s.userId]) providerByUser[s.userId] = s.provider;
          });
        })
        .catch((e) => console.error('Falha ao buscar sales:', e));

      const subsPromise = Promise.all(
        usersSnapshot.docs.map(async (userDoc) => {
          try {
            const snap = await getDoc(doc(db, 'users', userDoc.id, 'subscription', 'current'));
            if (snap.exists()) subByUser[userDoc.id] = snap.data();
          } catch { /* ignora subscription individual */ }
        })
      );

      await Promise.all([salesPromise, subsPromise]);

      const usersData: User[] = usersSnapshot.docs.map((userDoc) => {
        const userData = userDoc.data();
        const subData = subByUser[userDoc.id];
        let subscription;
        if (subData) {
          subscription = {
            plan: subData.plan || 'free',
            status: subData.status || 'inactive',
            provider: subData.provider,
            stripeSubscriptionId: subData.stripeSubscriptionId,
            lifetime: subData.lifetime === true,
          };
          if (!subscription.provider && subData.plan !== 'free') {
            subscription.provider = providerByUser[userDoc.id];
          }
        } else {
          subscription = { plan: 'free', status: 'inactive' };
        }
        return {
          id: userDoc.id,
          email: userData.email || 'Sem email',
          displayName: userData.displayName,
          createdAt: userData.createdAt?.toDate(),
          subscription,
        };
      });

      setUsers(usersData);
      setFilteredUsers(usersData);
    } catch (error: any) {
      console.error('Erro ao carregar usuários:', error);

      if (error?.code === 'permission-denied') {
        setError('Erro de permissão. Faça login no Firebase.');
      } else {
        setError('Erro ao carregar usuários: ' + (error?.message || 'Erro desconhecido'));
      }
    } finally {
      setLoading(false);
    }
  };

  const planKey = (plan?: string): 'free' | 'basic' | 'pro' => {
    const p = String(plan || 'free').toLowerCase();
    if (p === 'pro') return 'pro';
    if (p === 'free') return 'free';
    return 'basic';
  };

  const planLabelPt = (plan?: string) => ({ free: 'Grátis', basic: 'Basic', pro: 'Pro' }[planKey(plan)]);

  const exportCsv = () => {
    const header = ['Email', 'Nome', 'Plano', 'Status', 'Origem', 'Data de cadastro'];
    const rows = filteredUsers.map((u) => [
      u.email || '',
      u.displayName || '',
      planLabelPt(u.subscription?.plan),
      u.subscription?.status || '',
      u.subscription?.provider || '',
      u.createdAt ? u.createdAt.toLocaleDateString('pt-BR') : '',
    ]);
    const escape = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [header, ...rows].map((r) => r.map(escape).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hostly-usuarios-${planFilter}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    let filtered = users;
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.email.toLowerCase().includes(term) ||
          user.displayName?.toLowerCase().includes(term)
      );
    }
    if (planFilter !== 'all') {
      filtered = filtered.filter((u) => planKey(u.subscription?.plan) === planFilter);
    }
    filtered = filterByDateRange(filtered, (u) => u.createdAt, dateFrom, dateTo);
    setFilteredUsers(filtered);
  }, [searchTerm, planFilter, dateFrom, dateTo, users]);

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case 'pro':
        return '#4caf50';
      case 'premium':
      case 'basic':
        return '#ff9800';
      default:
        return '#9e9e9e';
    }
  };

  const getPlanLabel = (plan: string) => {
    switch (plan) {
      case 'premium':
        return 'BASIC';
      case 'pro':
        return 'PRO';
      case 'free':
        return 'FREE';
      default:
        return plan.toUpperCase();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'trialing':
        return 'success';
      case 'canceled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Ativo';
      case 'trialing':
        return '30 dias free';
      case 'canceled':
        return 'Cancelado';
      case 'inactive':
        return 'Inativo';
      default:
        return status;
    }
  };

  const getProviderLabel = (subscription: any) => {
    if (!subscription || subscription.plan === 'free') return '-';

    if (subscription.status === 'trialing') return 'Trial';

    if (subscription.provider === 'internal') return 'Cupom/Interno';

    if (subscription.provider === 'stripe' || subscription.stripeSubscriptionId) return 'Stripe';
    if (subscription.provider === 'apple') return 'Apple';

    if (!subscription.provider || subscription.provider === 'unknown') return '-';

    return subscription.provider;
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      plan: user.subscription?.plan || 'free',
      status: user.subscription?.status || 'inactive',
      durationInMonths: '1',
      lifetime: user.subscription?.lifetime === true,
    });
    setEditDialogOpen(true);
  };

  const handleSaveSubscription = async () => {
    if (!selectedUser) return;

    try {
      const subscriptionData: Record<string, any> = {
        ...buildManualSubscription({
          plan: editForm.plan,
          status: editForm.status,
          durationInMonths: editForm.durationInMonths,
          lifetime: editForm.lifetime,
        }),
        updatedAt: serverTimestamp(),
        updatedBy: 'admin',
      };

      await setDoc(
        doc(db, 'users', selectedUser.id, 'subscription', 'current'),
        subscriptionData,
        { merge: true }
      );

      const grant = editForm.lifetime && editForm.plan !== 'free' ? ' (vitalício)' : '';
      setSuccess(`Assinatura de ${selectedUser.email} atualizada com sucesso!${grant}`);
      setEditDialogOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err) {
      console.error('Erro ao atualizar assinatura:', err);
      setError('Erro ao atualizar assinatura');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress aria-label="Carregando" sx={{ color: theme.colors.primary }} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.colors.text.primary }}>
          Usuários
        </Typography>
        <IconButton
          onClick={loadUsers}
          aria-label="Recarregar usuários"
          sx={{
            color: theme.colors.primary,
            '&:hover': { backgroundColor: `${theme.colors.primary}15` },
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Buscar usuários por email ou nome..."
          inputProps={{ 'aria-label': 'Buscar usuários por email ou nome' }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.colors.text.secondary }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: theme.radii.medium,
              backgroundColor: theme.colors.card,
            },
          }}
        />
      </Box>

      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <TextField
          label="Cadastro de"
          type="date"
          size="small"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="até"
          type="date"
          size="small"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        {(dateFrom || dateTo) && (
          <Button size="small" onClick={() => { setDateFrom(''); setDateTo(''); }} sx={{ textTransform: 'none' }}>
            Limpar datas
          </Button>
        )}
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          {([
            { key: 'all', label: 'Todos' },
            { key: 'free', label: 'Grátis' },
            { key: 'basic', label: 'Basic' },
            { key: 'pro', label: 'Pro' },
          ] as const).map((opt) => (
            <Chip
              key={opt.key}
              label={opt.label}
              size="small"
              onClick={() => setPlanFilter(opt.key)}
              variant={planFilter === opt.key ? 'filled' : 'outlined'}
              sx={planFilter === opt.key
                ? { backgroundColor: theme.colors.primary, color: theme.colors.text.contrast }
                : { borderColor: theme.colors.border, color: theme.colors.text.secondary }}
            />
          ))}
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={exportCsv}
          disabled={filteredUsers.length === 0}
          sx={{ textTransform: 'none', borderColor: theme.colors.primary, color: theme.colors.primary }}
        >
          Exportar planilha
        </Button>
        <Box sx={{ flex: 1 }} />
        <Typography role="status" aria-live="polite" variant="body2" sx={{ color: theme.colors.text.secondary }}>
          Total: {filteredUsers.length} usuário(s)
        </Typography>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          backgroundColor: theme.colors.card,
          borderRadius: theme.radii.medium,
          border: `1px solid ${theme.colors.border}`,
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Plano</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Origem</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Data de Cadastro</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography sx={{ color: theme.colors.text.secondary }}>
                    {searchTerm ? 'Nenhum usuário encontrado' : 'Nenhum usuário cadastrado'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.displayName || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={getPlanLabel(user.subscription?.plan || 'free')}
                      size="small"
                      sx={{
                        backgroundColor: getPlanColor(user.subscription?.plan || 'free'),
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      {getProviderLabel(user.subscription)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(user.subscription?.status || 'inactive')}
                      size="small"
                      color={getStatusColor(user.subscription?.status || 'inactive')}
                    />
                  </TableCell>
                  <TableCell>
                    {user.createdAt
                      ? new Date(user.createdAt).toLocaleDateString('pt-BR')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleEditUser(user)}
                      aria-label={`Editar assinatura de ${user.email}`}
                      sx={{ color: theme.colors.primary }}
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de Edição de Assinatura */}
      <Dialog aria-labelledby="adminusers-dlg1-title" open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle id="adminusers-dlg1-title" sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">Editar Assinatura</Typography>
          {selectedUser && (
            <Typography variant="body2" color="text.secondary">
              {selectedUser.email}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Plano
            </Typography>
            <TextField
              select
              SelectProps={{ native: true }}
              value={editForm.plan}
              onChange={e => setEditForm({ ...editForm, plan: e.target.value })}
              fullWidth
            >
              <option value="free">Free (Gratuito)</option>
              <option value="premium">Basic (Plano intermediário)</option>
              <option value="pro">Pro (Plano completo)</option>
            </TextField>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Status
            </Typography>
            <TextField
              select
              SelectProps={{ native: true }}
              value={editForm.status}
              onChange={e => setEditForm({ ...editForm, status: e.target.value })}
              fullWidth
            >
              <option value="active">Ativo</option>
              <option value="trialing">Trial (30 dias)</option>
              <option value="canceled">Cancelado</option>
              <option value="inactive">Inativo</option>
            </TextField>
          </Box>

          {editForm.plan !== 'free' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={editForm.lifetime}
                  onChange={(e) => setEditForm({ ...editForm, lifetime: e.target.checked })}
                />
              }
              label="Vitalício (acesso permanente, sem expiração)"
            />
          )}

          {editForm.plan !== 'free' && editForm.status === 'active' && !editForm.lifetime && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Duração (em meses)
              </Typography>
              <TextField
                type="number"
                value={editForm.durationInMonths}
                onChange={e => setEditForm({ ...editForm, durationInMonths: e.target.value })}
                fullWidth
                inputProps={{ min: 1 }}
                helperText="A assinatura vai expirar após esse período"
              />
            </Box>
          )}

          <Alert severity="info">
            {editForm.lifetime && editForm.plan !== 'free'
              ? 'Acesso vitalício: o usuário fica com o plano permanentemente, marcado como "Interno".'
              : 'Essa alteração será feita manualmente e marcada como "Interno" na origem.'}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={() => setEditDialogOpen(false)} color="inherit">Cancelar</Button>
          <Button onClick={handleSaveSubscription} variant="contained">
            Salvar Alterações
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
